import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { SubscriptionProvider } from '@ubisend/pulse-subscribe';
import {
  Converse,
  useQuery,
  AuthContext,
  BotProvider,
  ThemeProvider,
  formatSettings,
  TicketsProvider,
  keepPreviousData,
  LanguageProvider,
  NotificationProvider,
  createInitialMessages,
  ConverseDeliveryProvider,
  SubscriberTypingProvider
} from '@ubisend/pulse-volt';

import { getConfig } from '../utils/index';

const { baseUrl, identifier, browserUrl, socketServer } = getConfig();

const getStart = data => (data.open ? '/window/body/messages' : '/');

const formatQueryData = data => {
  const { bot, styles } = formatSettings(data.settings);

  return {
    styles,
    bot: {
      ...bot,
      initialMessages: createInitialMessages(data.messages),
      initialShowCta: Boolean(data.call_to_action),
      ctaText: data.call_to_action
    },
    start: getStart(data),
    language: data.language,
    notifications: data.notifications,
    tickets: data.tickets
  };
};

const Bot = () => {
  const [token, setToken] = useState(null);
  const resultRef = useRef();

  const query = useQuery({
    queryKey: [
      'bot/converse/auth',
      { token }
    ],
    // Pulse params
    baseUrl,
    token: token,
    clientId: identifier,
    browserUrl,
    variables: [],
    channelDriverName: 'Converse',
    // Query params
    enabled: Boolean(
      !(token && resultRef.current) || resultRef.current !== token
    ),
    gcTime: 0,
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (query.isSuccess) {
      const data = query.data;
      resultRef.current = data.token;
      if (token !== data.token) {
        setToken(data.token);
      }
    }
  }, [query.isSuccess, query.data, token]);

  if (!query.isSuccess || !query.data.show) {
    return null;
  }

  const {
    bot,
    styles,
    language,
    notifications,
    tickets,
    start
  } = formatQueryData(query.data);

  return (
    <ThemeProvider value={styles}>
      <AuthContext.Provider value={{ token: query.data.token, setToken, query }}>
        <BotProvider {...bot}>
          <div
            style={{
              position: 'absolute',
              right: '1rem',
              bottom: '1rem',
              minWidth: '200px'
            }}>
            <ConverseDeliveryProvider
              client_id={identifier}
              browserUrl={browserUrl}
              variables={[]}
              base_url={baseUrl}>
              <LanguageProvider
                initialLanguage={language}
                namespaces={['bots', 'converse']}>
                <SubscriberTypingProvider>
                  <NotificationProvider initialNotifications={notifications}>
                    <TicketsProvider tickets={tickets}>
                      <Converse start={start} />
                    </TicketsProvider>
                  </NotificationProvider>
                </SubscriberTypingProvider>
              </LanguageProvider>
            </ConverseDeliveryProvider>
          </div>
        </BotProvider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
};

const SupportBot = () => {
  const location = useLocation();

  if (!location.pathname.startsWith('/docs')) {
    return null;
  }

  return (
    <SubscriptionProvider url={socketServer}>
      <Bot />
    </SubscriptionProvider>
  );
};

export default SupportBot;
export { Bot };
